// Manufacturing → Plants panel.
//
// Slice 7 layout (current):
//   Two columns side-by-side on desktop (50/50 split), stacking on
//   mobile. Left column = ManufacturingPlantsColumn (plants list +
//   persistent Unattached row + Dealers-attached box). Right column =
//   ManufacturingPlantDealerLocationsTabbedSection (slice-5 Dealer
//   locations slider with App-using / Non-app tabs).
//
// The old two-pane "plants left, attached-dealers right" layout was
// retired in slice 7 along with ManufacturingPlantDealersPane. Mobile
// no longer has a separate drill-in shell — the column stacks
// naturally inside a narrow viewport and the user scrolls.
//
// Dealer roster admin (subscriber dealers + non-app locations) lives
// here as of the Plants↔Dealers union slice — the previous Logistics
// → Dealers mode was retired in the same slice. See
// docs/planning/plants_dealers_union.md.
import { useEffect, useRef, useState } from "react";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { ManufacturingPlantDealerLocationsTabbedSection } from "./ManufacturingPlantDealerLocationsTabbedSection";
import { ManufacturingPlantsColumn } from "./ManufacturingPlantsColumn";
import {
  UNATTACHED_PLANT_SENTINEL,
  isRealPlantSelection,
  type PlantSelection,
} from "./plantDealersDrafts";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";
import { useManufacturingPlantSubscriberLocationCounts } from "./useManufacturingPlantSubscriberLocationCounts";
import { useManufacturingPlantsTab } from "./useManufacturingPlantsTab";

export function ManufacturingPlantsPanel() {
  const gate = useManufacturingEntitlementGate();

  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const plantsTab = useManufacturingPlantsTab(navTick, newSignal);

  // Selection lives at the panel so both columns stay in sync. We
  // auto-select the default plant on initial load (so the dealers
  // attached box has content immediately); explicit user picks win
  // after that.
  const [selection, setSelection] = useState<PlantSelection>(null);

  const dealersTab = useManufacturingPlantDealersTab(navTick, selection);
  const subscriberLocationCounts = useManufacturingPlantSubscriberLocationCounts(
    plantsTab.plants,
    navTick
  );

  // Slice 9: the auto-select-default-plant effect only runs ONCE,
  // on initial load — the user's subsequent clicks (including
  // click-to-deselect, which now toggles to the Unattached
  // sentinel rather than null) win. Without this guard the panel
  // would race the column's unattached toggle and re-select the
  // default plant on every render where selection happened to be
  // null, defeating the deselect interaction.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (plantsTab.plants.length === 0) return;
    if (selection !== null) {
      hasAutoSelectedRef.current = true;
      return;
    }
    hasAutoSelectedRef.current = true;
    const def = plantsTab.plants.find((p) => p.is_default);
    setSelection(def?.id ?? plantsTab.plants[0].id);
  }, [selection, plantsTab.plants]);

  const activePlant = isRealPlantSelection(selection)
    ? plantsTab.plants.find((p) => p.id === selection) ?? null
    : null;
  const activePlantLabel = activePlant
    ? `${activePlant.plant_code} · ${activePlant.name}`
    : selection === UNATTACHED_PLANT_SENTINEL
      ? "Unattached"
      : "";

  const errorBanner =
    gate.enabled && (plantsTab.localError || dealersTab.localError) ? (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
        {plantsTab.localError || dealersTab.localError}
      </div>
    ) : null;

  const refreshBusy =
    plantsTab.listBusy || plantsTab.actionBusy || dealersTab.listBusy || dealersTab.actionBusy;

  return (
    <div className="w-full text-left space-y-4">
      <ManufacturingGateBanner gate={gate} />
      {errorBanner}

      {gate.enabled ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="min-w-0">
            <ManufacturingPlantsColumn
              plantsTab={plantsTab}
              dealersTab={dealersTab}
              selection={selection}
              onSelect={setSelection}
              rosterCounts={dealersTab.rosterCounts}
              subscriberLocationCounts={subscriberLocationCounts}
              activePlantLabel={activePlantLabel}
              onRequestNewPlant={() => setNewSignal((s) => s + 1)}
              onRequestRefresh={() => {
                gate.refresh();
                setNavTick((s) => s + 1);
              }}
              refreshBusy={refreshBusy}
              gateEnabled={gate.enabled}
              gateLoading={gate.loading}
            />
          </div>
          <div className="min-w-0">
            <ManufacturingPlantDealerLocationsTabbedSection
              plants={plantsTab.plants}
              refreshSignal={navTick}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
